/**
 * InsiderTab - Insider trades table with time filters
 */
import { useState } from 'react'
import { TradeRow } from './TradeRow'
import { useInsiderTrades, useInsiderSummary } from '../../hooks/useResearch'

interface InsiderTabProps {
  symbol: string
}

type TimeFilter = 30 | 90 | 365 | 0 // 0 = all time

const TIME_FILTER_OPTIONS: { value: TimeFilter; label: string }[] = [
  { value: 30, label: '30 Days' },
  { value: 90, label: '90 Days' },
  { value: 365, label: '1 Year' },
  { value: 0, label: 'All' },
]

function SummaryStat({
  label,
  value,
  variant = 'default',
}: {
  label: string
  value: string | number
  variant?: 'default' | 'positive' | 'negative'
}) {
  const variantStyles = {
    default: 'text-gray-900',
    positive: 'text-green-600',
    negative: 'text-red-600',
  }

  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className={`text-2xl font-semibold ${variantStyles[variant]}`}>{value}</p>
    </div>
  )
}

function TradeSkeleton() {
  return (
    <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 animate-pulse">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="h-5 w-32 bg-gray-200 rounded" />
            <div className="h-5 w-12 bg-gray-200 rounded" />
          </div>
          <div className="h-4 w-24 bg-gray-200 rounded mt-2" />
        </div>
        <div className="flex gap-4">
          <div className="h-5 w-20 bg-gray-200 rounded" />
          <div className="h-5 w-20 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  )
}

function formatShares(shares: number): string {
  const absShares = Math.abs(shares)
  if (absShares >= 1_000_000) {
    return `${(absShares / 1_000_000).toFixed(2)}M`
  }
  if (absShares >= 1_000) {
    return `${(absShares / 1_000).toFixed(1)}K`
  }
  return absShares.toLocaleString()
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--'
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000_000) {
    return `$${(absValue / 1_000_000_000).toFixed(2)}B`
  }
  if (absValue >= 1_000_000) {
    return `$${(absValue / 1_000_000).toFixed(2)}M`
  }
  if (absValue >= 1_000) {
    return `$${(absValue / 1_000).toFixed(1)}K`
  }
  return `$${absValue.toLocaleString()}`
}

export function InsiderTab({ symbol }: InsiderTabProps) {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(90)

  const {
    data: summary,
    isLoading: summaryLoading,
  } = useInsiderSummary(symbol, timeFilter === 0 ? undefined : timeFilter)

  const {
    data: tradesData,
    isLoading: tradesLoading,
    error,
  } = useInsiderTrades(symbol, {
    days: timeFilter === 0 ? undefined : timeFilter,
    limit: 100,
  })

  const trades = tradesData?.trades ?? []
  const isLoading = summaryLoading || tradesLoading

  return (
    <div className="space-y-6">
      {/* Time filter buttons */}
      <div className="flex flex-wrap gap-2">
        {TIME_FILTER_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setTimeFilter(option.value)}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              timeFilter === option.value
                ? 'bg-primary-600 text-white shadow-sm'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Summary stats */}
      {summaryLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-4 animate-pulse">
              <div className="h-4 w-20 bg-gray-200 rounded mx-auto mb-2" />
              <div className="h-8 w-16 bg-gray-200 rounded mx-auto" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-3 gap-4">
          <SummaryStat
            label="Net Shares"
            value={
              summary.net_shares > 0
                ? `+${formatShares(summary.net_shares)}`
                : summary.net_shares < 0
                  ? `-${formatShares(summary.net_shares)}`
                  : '0'
            }
            variant={
              summary.net_shares > 0
                ? 'positive'
                : summary.net_shares < 0
                  ? 'negative'
                  : 'default'
            }
          />
          <SummaryStat
            label="Total Buys"
            value={`${summary.buy_count} (${formatCurrency(summary.total_buy_value)})`}
            variant="positive"
          />
          <SummaryStat
            label="Total Sells"
            value={`${summary.sell_count} (${formatCurrency(summary.total_sell_value)})`}
            variant="negative"
          />
        </div>
      ) : null}

      {/* Error state */}
      {error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
          No insider trading data available for this symbol.
        </div>
      )}

      {/* Loading state for trades */}
      {tradesLoading && (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <TradeSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Trades list */}
      {!isLoading && !error && (
        <>
          {trades.length > 0 ? (
            <div className="space-y-3">
              {trades.map((trade) => (
                <TradeRow key={trade.id} trade={trade} />
              ))}
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">No insider trades</h3>
              <p className="mt-2 text-sm text-gray-500">
                {timeFilter === 0
                  ? 'No insider trading activity has been recorded for this company.'
                  : `No insider trades in the last ${timeFilter} days. Try selecting a longer time period.`}
              </p>
            </div>
          )}

          {/* Results count */}
          {trades.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {trades.length} trade{trades.length !== 1 ? 's' : ''}
              {timeFilter !== 0 ? ` from the last ${timeFilter} days` : ''}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default InsiderTab
